import React, { useState } from 'react';
import {
    Button,
    Checkbox,
    FormControlLabel,
    Grid,
    Paper,
} from '@material-ui/core';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import CustomInput from '../../../../../components/CustomInput';
import { issueTrackerRuleTypeEnum, issueTrackerRuleTypeList, issueTrackerRuleValueMapping, ruleOperands, ruleOperandsEnum } from '../IssueTrackerConfigConsts';
import { issueTrackerConfigStyles } from '../style';
import DropdownFilter from 'components/DropdownFilter';
import { useGetUsersQuery } from 'features/users/usersSlice';
import { IssueTrackerUser } from 'features/IssuesTracker/IssueTrackerUser/IssueTrackerUser';
import usePrompt from 'hooks/usePrompt';
import NotificationProvider from 'components/NotificationProvider';
import { useDeleteIssueTrackerRuleMutation, useUpdateIssueTrackerRuleMutation } from '../IssueTrackerRuleSlice';
import Authorize from 'components/Authorize';
import { permissionProfiles } from 'components/Authorize/permissionProfiles';
import { arrayToObject } from 'utils/object-util';
import { Alert } from '@mui/material';
import RuleConditions from './RuleConditions';
import { hasAccessToAllClients } from 'features/user/userSlice';
import { useSelector } from 'react-redux';


const IssueTrackerRule = ({ rule, onCancel, rulesList, clientConfig }) => {
    const classes = issueTrackerConfigStyles();
    const { triggerPrompt } = usePrompt();

    let canSeeAllClients = useSelector(hasAccessToAllClients);

    let { data: users, error: usersError, isLoading: isLoadingUsers } = useGetUsersQuery();
    users = users || [];

    let usersLookup = arrayToObject(users);

    let [deleteRule, { isLoading: isDeleting }] = useDeleteIssueTrackerRuleMutation();
    let [updateRule, { isLoading: isUpdating }] = useUpdateIssueTrackerRuleMutation();

    const [ruleToEdit, setRuleToEdit] = useState({ ...rule });

    const issueTrackerRuleTypeValue = {
        [issueTrackerRuleTypeEnum.Slack]: <CustomInput
            label="Slack Url"
            elementType="text"
            value={ruleToEdit?.slackUrl}
            onChange={val => onFieldChange("slackUrl", val)}
            showEmpty={true}
            required
        />,
        [issueTrackerRuleTypeEnum.Assignee]: <DropdownFilter
            withTypeAhead
            id="assignee"
            title="Assignee"
            value={usersLookup?.[ruleToEdit?.userId]?.id ?? ''}
            onSelect={(val) => onFieldChange('userId', val?.id ?? '')}
            elementType="dropdown"
            values={users ?? []}
            showEmpty
            renderOption={(option) => <IssueTrackerUser userId={option?.id} />}
            required
            shrink
        />,
        [issueTrackerRuleTypeEnum.Email]: <CustomInput
            label="Email"
            elementType="text"
            value={ruleToEdit?.email}
            onChange={val => onFieldChange("email", val)}
            showEmpty={true}
            required
        />,
    }

    const onFieldChange = (field, val) => {
        if (field == "ruleType") {
            setRuleToEdit({ ...ruleToEdit, [field]: val, [issueTrackerRuleValueMapping[val]]: '' })
        }
        // If the rule operand is changed to "And", remove duplicate conditions based on the field
        else if (field == "ruleOperand" && val == ruleOperandsEnum.And) {
            let conditions = [...(ruleToEdit?.conditionList ?? [])];
            let uniqueConditions = conditions.filter((condition, index, self) =>
                index === self.findIndex(c => c.field === condition.field)
            );
            setRuleToEdit({ ...ruleToEdit, [field]: val, conditionList: uniqueConditions });
        }
        else setRuleToEdit({ ...ruleToEdit, [field]: val })
    }

    const handleSave = async () => {
        let ruleToSave = {
            ...ruleToEdit,
            conditionListObj: ruleToEdit?.conditionList,
        };
        let res = await updateRule(ruleToSave);

        if (!res?.error) {
            NotificationProvider.success('Saved successfully');
            onCancel();
        } else {
            NotificationProvider.error('Failed to save');
        }
    }

    const onDelete = async () => {
        triggerPrompt({
            title: "Delete Rule",
            content: "Are you sure you want to delete the rule?",
            onConfirm: async () => {
                const res = await deleteRule({
                    id: ruleToEdit?.id
                })

                if (!res?.error) {
                    NotificationProvider.success('Successfully deleted rule');
                } else {
                    NotificationProvider.error('Failed to delete rule');
                }
            },
        });
    }

    const cancel = () => {
        if (ruleToEdit?.isNew) {
            onCancel();
        }
        else {
            setRuleToEdit(rule)
        }
    }

    const isValid = () => {
        if (ruleToEdit?.ruleType &&
            ruleToEdit?.[issueTrackerRuleValueMapping[ruleToEdit.ruleType]] &&
            ruleToEdit?.conditionList?.length > 0 &&
            ruleToEdit?.conditionList?.every(c => c.field && c.value)) {
            return true;
        }
        return false;
    }


    /**
     * Checks if the current rule being edited is a duplicate of any existing "Assignee" rules in the rules list.
     * 
     * The function filters the rules list to exclude the rule being edited and focuses on rules of type "Assignee".
     * It then compares the conditions of the current rule with the filtered rules based on the rule operand:
     * - If the current rule uses the "Or" operand, it checks for overlapping conditions with other "Or" rules 
     *   or single-condition "And" rules.
     * - If the current rule uses the "And" operand, it checks for overlapping conditions with "Or" rules 
     *   or complete matches with other "And" rules.
     */
    const isDuplicateAssigneeRule = () => {
        if (!isValid()) return false;
        if (ruleToEdit?.ruleType != issueTrackerRuleTypeEnum.Assignee) return false;
        let otherAssigneeRules = rulesList?.filter(r => r.id != ruleToEdit?.id && r.ruleType == issueTrackerRuleTypeEnum.Assignee)
        if (otherAssigneeRules?.length > 0) {
            if (ruleToEdit?.ruleOperand == ruleOperandsEnum.Or) {
                return otherAssigneeRules?.some(r => (r.ruleOperand == ruleOperandsEnum.Or &&
                    r.conditionList?.some(rc =>
                        ruleToEdit?.conditionList?.some(c => c.field === rc.field && c.value === rc.value)))
                    || (r.ruleOperand == ruleOperandsEnum.And &&
                        r?.conditionList?.length == 1 &&
                        ruleToEdit?.conditionList?.some(c => c.field === r.conditionList[0].field && c.value === r.conditionList[0].value))
                );
            }
            else if (ruleToEdit?.ruleOperand == ruleOperandsEnum.And) {
                return otherAssigneeRules?.some(r => (r.ruleOperand == ruleOperandsEnum.Or &&
                    r.conditionList?.some(rc =>
                        ruleToEdit?.conditionList?.some(c => c.field === rc.field && c.value === rc.value)))
                    || (r.ruleOperand == ruleOperandsEnum.And &&
                        r?.conditionList?.every(rc =>
                            ruleToEdit?.conditionList?.some(c => c.field === rc.field && c.value === rc.value)))
                );
            }
        }
        return false;
    }

    return (
        <Paper className={`${classes.listItemWrapper} ${ruleToEdit?.isNew ? classes.listItemWrapperNew : ""}`}>
            <Grid className={classes.ruleWrapper} container spacing={1}>
                <Grid item xs={6}>
                    <Authorize profile={permissionProfiles.ISSUETRACKER.VIEW_CONFIG}>
                        <CustomInput
                            label="Rule Type"
                            elementType="dropdown"
                            values={issueTrackerRuleTypeList}
                            value={ruleToEdit?.ruleType}
                            onChange={val => onFieldChange("ruleType", +val)}
                            showEmpty={true}
                            required
                        />
                    </Authorize>
                </Grid>
                <Grid item xs={5}>
                    <Authorize profile={permissionProfiles.ISSUETRACKER.VIEW_CONFIG}>
                        {issueTrackerRuleTypeValue[ruleToEdit?.ruleType]}
                    </Authorize>
                </Grid>
                <Grid item xs={12} className={classes.conditionsTitle}>Conditions:</Grid>
                <Grid item xs={12} container spacing={1}>
                    <Grid item xs={3}>
                        <Authorize profile={permissionProfiles.ISSUETRACKER.VIEW_CONFIG}>
                            <CustomInput
                                label="Rule Operand"
                                elementType="dropdown"
                                value={ruleToEdit?.ruleOperand}
                                values={ruleOperands}
                                onChange={val => onFieldChange('ruleOperand', parseInt(val))}
                            />
                        </Authorize>
                    </Grid>
                </Grid>

                <RuleConditions ruleToEdit={ruleToEdit} onFieldChange={onFieldChange} />

                {canSeeAllClients &&
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Authorize profile={permissionProfiles.ISSUETRACKER.VIEW_CONFIG}>
                                    <Checkbox
                                        defaultChecked={false}
                                        checked={!ruleToEdit?.clientAccountId}
                                        onChange={(e, value) => onFieldChange('clientAccountId', value ? null : clientConfig.accountId)}
                                    />
                                </Authorize>
                            }
                            label={<div className={classes.generateLabel}>Apply to all clients</div>}
                        />
                    </Grid>
                }
            </Grid>
            {
                isDuplicateAssigneeRule() &&
                <Grid container xs={12} justifyContent='flex-end' className={classes.warningMessage}>
                    <Alert severity="warning">There is another assignee rule that matches the same conditions</Alert>
                </Grid>
            }

            <Grid container className={classes.btnContainer}>
                <Grid item>
                    <Authorize profile={permissionProfiles.ISSUETRACKER.SAVE_CONFIG}>
                        <Button
                            onClick={cancel}
                            className={classes.button}
                            color="primary"
                        >
                            Cancel
                        </Button>
                        {!ruleToEdit?.isNew && <Button
                            onClick={onDelete}
                            className={classes.button}
                            variant="outlined"
                            color="primary"
                            endIcon={<DeleteOutlineIcon />}
                            disabled={isDeleting}
                        >
                            {(isDeleting) ? "Deleting..." : "Delete"}
                        </Button>}
                        <Button
                            className={classes.saveButton}
                            onClick={handleSave}
                            color="primary"
                            variant="contained"
                            disabled={!isValid() || isUpdating}
                        >
                            {isUpdating ? 'Saving...' : 'Save'}
                        </Button>
                    </Authorize>
                </Grid>
            </Grid>
        </Paper >
    );
};

export default IssueTrackerRule;
